import { useTranslation } from "react-i18next";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { LegalDocumentType } from "@/components/features/notarial-requests/legal-entity-request-fields";
import { CertificateInvoicePreferencesFields } from "@/components/shared/CertificateInvoicePreferencesFields";
import { CertificateWizardPricingBreakdown } from "@/components/shared/CertificateWizardPricingBreakdown";
import { Separator } from "@/components/ui/separator";
import { certificateCatalogDisplayLabel } from "@/lib/certificateCatalogDisplayLabel";
import type { ValidateRutResponse } from "@/services/authService";
import type { CertificatePricingCatalogItem } from "@/services/certificatePricingCatalogService";

function SummaryRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-4">
      <span className="shrink-0 text-sm font-medium text-foreground sm:w-48">{label}</span>
      <span className="min-w-0 text-sm text-muted-foreground">{value}</span>
    </div>
  );
}

export function LegalEntityRequestSummary({
  rutDigits,
  validatedCompany,
  legalDocumentType,
  estatutoFile,
  comunicacionBcuFile,
  legalDocumentFrontFile,
  legalDocumentBackFile,
  passportDocumentFile,
  totalChargeLabel,
  catalogItem,
  fulfillmentMode,
  invoiceIncludeRut,
  onInvoiceIncludeRutChange,
  invoiceRutDigits,
  onInvoiceRutDigitsChange,
  invoiceRutLockedFromRegistration,
  companyRutUnverified,
}: {
  rutDigits: string;
  validatedCompany: ValidateRutResponse;
  legalDocumentType: LegalDocumentType;
  estatutoFile: File;
  comunicacionBcuFile: File;
  legalDocumentFrontFile: File | null;
  legalDocumentBackFile: File | null;
  passportDocumentFile: File | null;
  /** Resolved catalog price (e.g. UYU 3.824,06) shown before payment. */
  totalChargeLabel?: string | null;
  catalogItem?: CertificatePricingCatalogItem;
  fulfillmentMode?: string;
  invoiceIncludeRut: boolean;
  onInvoiceIncludeRutChange: (value: boolean) => void;
  invoiceRutDigits: string;
  onInvoiceRutDigitsChange: (digits: string) => void;
  /** Company account at registration: RUT on invoice is mandatory (no opt-out). */
  invoiceRutLockedFromRegistration?: boolean;
  companyRutUnverified?: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  const rutDisplay = rutDigits.replace(/\D/g, "").slice(0, 12);
  const docTypeLabel =
    legalDocumentType === "national_id"
      ? t("requests.user.legalDocumentNationalId")
      : t("requests.user.legalDocumentPassport");

  const certificateLabel = certificateCatalogDisplayLabel(catalogItem, t);

  return (
    <div className="space-y-6">
      {companyRutUnverified === true ? (
        <Alert variant="default" className="border-amber-500/50 bg-amber-500/10 dark:border-amber-500/40">
          <AlertTitle className="text-foreground">{t("requests.user.manualCompanySummaryNoticeTitle")}</AlertTitle>
          <AlertDescription className="text-muted-foreground">
            {t("requests.user.manualCompanySummaryNoticeBody")}
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="wizard-summary-panel space-y-4 p-4 sm:p-5">
        <SummaryRow
          label={t("requests.user.wizard.summaryCertificateLabel")}
          value={certificateLabel}
        />
        <SummaryRow label={t("requests.user.legalEntityRutLabel")} value={rutDisplay} />
        <SummaryRow label={t("requests.user.legalEntitySummary.companyName")} value={validatedCompany.legalName} />
        {validatedCompany.legalAddress ? (
          <SummaryRow
            label={t("requests.user.legalEntitySummary.address")}
            value={validatedCompany.legalAddress}
          />
        ) : null}
      </div>

      <Separator />

      <div className="wizard-summary-panel space-y-3 p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-foreground">
          {t("requests.user.legalEntityRequiredDocs")}
        </h3>
        <div className="space-y-3">
          <SummaryRow
            label={t("requests.user.requestTypes.document_estatuto")}
            value={estatutoFile.name}
          />
          <SummaryRow
            label={t("requests.user.requestTypes.document_comunicacion_bcu")}
            value={comunicacionBcuFile.name}
          />
        </div>
      </div>

      <Separator />

      <div className="wizard-summary-panel space-y-3 p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-foreground">
          {t("requests.user.legalDocumentTypeLabel")}
        </h3>
        <SummaryRow label={t("requests.user.legalEntitySummary.idType")} value={docTypeLabel} />
        {legalDocumentType === "national_id" && legalDocumentFrontFile && legalDocumentBackFile ? (
          <div className="mt-3 space-y-3">
            <SummaryRow
              label={t("requests.user.requestTypes.document_legal_document_front")}
              value={legalDocumentFrontFile.name}
            />
            <SummaryRow
              label={t("requests.user.requestTypes.document_legal_document_back")}
              value={legalDocumentBackFile.name}
            />
          </div>
        ) : null}
        {legalDocumentType === "passport" && passportDocumentFile ? (
          <div className="mt-3 space-y-3">
            <SummaryRow
              label={t("requests.user.requestTypes.document_passport_document")}
              value={passportDocumentFile.name}
            />
          </div>
        ) : null}
      </div>

      {totalChargeLabel ? (
        <>
          <Separator />
          <CertificateWizardPricingBreakdown
            catalogItem={catalogItem}
            variant="flat"
            totalChargeLabel={totalChargeLabel ?? null}
            fulfillmentMode={fulfillmentMode}
          />
        </>
      ) : null}

      <Separator />

      <CertificateInvoicePreferencesFields
        includeRut={invoiceIncludeRut}
        onIncludeRutChange={onInvoiceIncludeRutChange}
        rutDigits={invoiceRutDigits}
        onRutDigitsChange={onInvoiceRutDigitsChange}
        rutLockedFromRegistration={invoiceRutLockedFromRegistration === true}
      />
    </div>
  );
}
